import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import TeamBuilder from '../components/TeamBuilder';
import CreateLeagueModal from '../components/CreateLeagueModal';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../hooks/AuthContext';
import {
  parseScheduleInstantAsUtc,
  formatScheduleTimeUtc,
  formatScheduleTimeBrowserLocal,
  formatScheduleDateBrowserLocal
} from '../utils/scheduleTime';

// Helper function to format date (YYYY-MM-DD) to a readable format
const formatDate = (dateString: string) => {
  if (!dateString) return 'TBD';
  // Extract just the date part if it includes time
  const dateOnly = dateString.split('T')[0];
  const date = new Date(dateOnly);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

interface LiveTournament {
  id: number;
  name: string;
  format: string;
  year: number;
  status: string;
  start_date: string;
  end_date: string;
  description?: string;
  country?: string;
  organizer?: string;
  teams_visible?: boolean;
  schedule_visible?: boolean;
}



interface Team {
  id: number;
  name: string;
  short_name: string;
  country: string;
  logo_url?: string;
}

interface Player {
  id: number;
  name: string;
  team_id: number;
  team_name: string;
  role: string;
  batting_style?: string;
  bowling_style?: string;
  is_foreign?: boolean;
  stats?: {
    matches: number;
    runs: number;
    wickets: number;
    catches: number;
  };
}

interface Match {
  id: number;
  team1_id: number;
  team2_id: number;
  team1_name: string;
  team2_name: string;
  match_date: string;
  /** ISO UTC instant when available (public /live/:id/matches) */
  match_datetime?: string;
  match_time?: string;
  match_time_local?: string;
  venue: string;
  status: string;
  result?: string;
}

interface PointsTableRow {
  position: number;
  team: string;
  pts: number | null;
  nrr?: string | null;
  /** Matches played */
  pld?: number | null;
  won?: number | null;
  lost?: number | null;
  tied?: number | null;
  /** No-result games */
  nr?: number | null;
}

interface PointsTableSnapshot {
  id: number;
  captured_at: string;
  source: string;
  rows: PointsTableRow[];
}

/** Prefer UTC instant so "your time" uses the browser, not the API server timezone. */
function getMatchScheduleLabels(match: Match): {
  dateLabel: string;
  utcLine: string | null;
  localLine: string | null;
} {
  const raw = match.match_datetime || match.match_date;
  const d = parseScheduleInstantAsUtc(raw ?? '');
  if (d) {
    return {
      dateLabel: formatScheduleDateBrowserLocal(d),
      utcLine: `${formatScheduleTimeUtc(d)} UTC`,
      localLine: formatScheduleTimeBrowserLocal(d)
    };
  }
  return {
    dateLabel: formatDate(match.match_date),
    utcLine: match.match_time ? `${match.match_time} UTC` : null,
    localLine: match.match_time_local ?? null
  };
}

// Role icon consistent with add-players list (PlayerPickerModal / BudgetLeaguePage)
const getRoleIcon = (role?: string): React.ReactNode => {
  if (!role) return '👤';
  const r = role.trim();
  switch (r) {
    case 'Batsman':
      return (
        <img
          src="/images/baticon.png"
          alt="Bat"
          className="inline-block w-10 h-10 object-contain"
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        />
      );
    case 'Bowler':
      return '🥎';
    case 'WK-Batsman':
      return '🧤';
    case 'All-Rounder':
      return '🏏';
    default:
      break;
  }
  const lower = r.toLowerCase();
  if (lower.includes('wk')) return '🧤';
  if (lower.includes('bat')) return <img src="/images/baticon.png" alt="Bat" className="inline-block w-10 h-10 object-contain" style={{ display: 'inline-block', verticalAlign: 'middle' }} />;
  if (lower.includes('bow')) return '🥎';
  if (lower.includes('all')) return '🏏';
  return '👤';
};

const TournamentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'schedule' | 'teams' | 'teams_squad' | 'points_table'>('teams');
  const [tournament, setTournament] = useState<LiveTournament | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [pointsSnapshot, setPointsSnapshot] = useState<PointsTableSnapshot | null | undefined>(undefined);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsError, setPointsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [selectedTeamTab, setSelectedTeamTab] = useState<string>('');
  const [showCreateLeagueModal, setShowCreateLeagueModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [userTimezone, setUserTimezone] = useState<string>('');
  
  // Track which data has been loaded to avoid redundant API calls
  const [loadedData, setLoadedData] = useState<Set<string>>(new Set());

  // Add state for role filter
  const [selectedRole, setSelectedRole] = useState<string>('All');

  // Set default team tab when teams are available
  useEffect(() => {
    if (players && players.length > 0 && !selectedTeamTab) {
      const teamsList = Array.from(new Set(players.map((player: any) => player.team_name))).sort();
      if (teamsList.length > 0) {
        setSelectedTeamTab(teamsList[0]);
      }
    }
  }, [players, selectedTeamTab]);

  useEffect(() => {
    if (id) {
      fetchTournamentDetails();
    }
    
    // Detect user's timezone
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(timezone);
      console.log('🌍 User timezone detected:', timezone);
    } catch (error) {
      console.error('Error detecting timezone:', error);
      setUserTimezone('Unknown');
    }
  }, [id]);

  // Auto-switch to first visible tab when tournament loads
  useEffect(() => {
    if (tournament) {
      const visibleTabs = [
        { id: 'teams', visible: tournament.teams_visible !== false },
        { id: 'schedule', visible: tournament.schedule_visible !== false },
        { id: 'points_table', visible: true },
        { id: 'teams_squad', visible: true }
      ].filter(tab => tab.visible);
      
      // If current tab is not visible, switch to first visible tab
      const currentTabVisible = visibleTabs.some(tab => tab.id === activeTab);
      if (!currentTabVisible && visibleTabs.length > 0) {
        setActiveTab(visibleTabs[0].id as any);
      }
    }
  }, [tournament, activeTab]);

  const fetchTournamentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) {
        setError('Tournament ID is required');
        setLoading(false);
        return;
      }

      // Fetch tournament details (always needed)
      const tournamentId = parseInt(id);
      const tournamentData = await apiService.getLiveTournamentById(tournamentId);
      
      if (tournamentData.success && tournamentData.liveTournament) {
        setTournament(tournamentData.liveTournament);
      } else {
        setError(tournamentData.message || 'Tournament not found or not available');
        console.error('Tournament data response:', tournamentData);
      }

      setLoadedData(prev => new Set([...Array.from(prev), 'tournament']));
    } catch (err) {
      setError('Failed to load tournament details. Please try again.');
      console.error('Error fetching tournament details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load teams data only when needed
  const loadTeamsData = async () => {
    if (loadedData.has('teams')) {
      console.log('🔍 Teams already loaded, skipping API call');
      return;
    }

    try {
      if (!id) return;
      console.log('🔍 Loading teams data...');
      const tournamentId = parseInt(id);
      const teamsData = await apiService.getTournamentTeams(tournamentId);
      
      if (teamsData.success) {
        setTeams(teamsData.teams);
        setLoadedData(prev => new Set([...Array.from(prev), 'teams']));
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  // Load players data only when needed
  const loadPlayersData = async () => {
    if (loadedData.has('players')) {
      console.log('🔍 Players already loaded, skipping API call');
      return;
    }

    try {
      if (!id) return;
      console.log('🔍 Loading players data...');
      const tournamentId = parseInt(id);
      const playersData = await apiService.getTournamentPlayers(tournamentId);
      
      if (playersData.success) {
        setPlayers(playersData.players);
        setLoadedData(prev => new Set([...Array.from(prev), 'players']));
      }
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  // Load matches data only when needed
  const loadMatchesData = async () => {
    if (loadedData.has('matches')) {
      console.log('🔍 Matches already loaded, skipping API call');
      return;
    }

    try {
      if (!id) return;
      console.log('🔍 Loading matches data...');
      const tournamentId = parseInt(id);
      const matchesData = await apiService.getTournamentMatches(tournamentId);
      
      if (matchesData.success) {
        setMatches(matchesData.matches);
        setLoadedData(prev => new Set([...Array.from(prev), 'matches']));
      }
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
  };

  const loadPointsTableData = async () => {
    if (loadedData.has('points_table')) {
      return;
    }
    try {
      if (!id) return;
      setPointsLoading(true);
      setPointsError(null);
      const tournamentId = parseInt(id, 10);
      const data = await apiService.getLiveTournamentPointsTable(tournamentId);
      if (data.success) {
        setPointsSnapshot(data.snapshot ?? null);
        setLoadedData((prev) => new Set([...Array.from(prev), 'points_table']));
      } else {
        setPointsError(data.message || 'Could not load standings');
        setPointsSnapshot(null);
      }
    } catch (err: any) {
      console.error('Error fetching points table:', err);
      setPointsError(err?.message || 'Could not load standings');
      setPointsSnapshot(null);
    } finally {
      setPointsLoading(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (!id) return;
    
    switch (activeTab) {
      case 'teams':
        loadTeamsData();
        break;
      case 'teams_squad':
        loadPlayersData();
        break;
      case 'schedule':
        loadMatchesData();
        break;
      case 'points_table':
        loadPointsTableData();
        break;
    }
  }, [activeTab, id]);

  const getFilteredPlayers = () => {
    if (selectedTeam) {
      return players.filter(player => player.team_id === selectedTeam);
    }
    return players;
  };

  const handleCreateLeague = () => {
    console.log('🔍 Create League clicked (TournamentDetails)');
    console.log('🔍 Current auth state - isAuthenticated:', isAuthenticated);
    console.log('🔍 Current auth state - user:', user);
    console.log('🔍 Token in localStorage:', localStorage.getItem('authToken') ? 'exists' : 'missing');
    console.log('🔍 useAuth hook state:', { user, isAuthenticated, isLoading });
    
    // Wait for authentication to finish loading
    if (isLoading) {
      console.log('🔍 Authentication still loading, please wait...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔍 User not authenticated, showing login modal');
      setShowLoginModal(true);
      return;
    }
    console.log('🔍 User authenticated, showing create league modal');
    setShowCreateLeagueModal(true);
  };

  const handleLeagueCreated = () => {
    // Could refresh data if needed
    alert('League created successfully! You can view it in the Tournaments page.');
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // After successful login, show the create league modal
    setShowCreateLeagueModal(true);
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      await login(email, password);
      // Add a small delay to ensure authentication state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('🔍 After login - checking auth state again');
      console.log('🔍 Token in localStorage:', localStorage.getItem('authToken') ? 'exists' : 'missing');
      // After successful login, show the create league modal
      setShowLoginModal(false);
      setShowCreateLeagueModal(true);
    } catch (error) {
      // Error is handled by the LoginModal component
      throw error;
    }
  };

  const handleSwitchToRegister = () => {
    // For now, just close login modal and show a message
    setShowLoginModal(false);
    alert('Please register first. You can do this from the main navigation.');
  };

  if (loading) {
    return <div className="text-center py-12 text-white">Loading tournament details...</div>;
  }

  if (error || !tournament) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-xl mb-2">⚠️</div>
        <p className="text-white mb-4">{error || 'Tournament not found'}</p>
        <button 
          onClick={() => navigate('/tournaments')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate('/tournaments')}
          className="mb-4 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          ← Back to Tournaments
        </button>
        <h1 className="text-3xl font-bold text-white mb-2">{tournament.name} ({tournament.format} {tournament.year})</h1>
        <p className="text-white/60">{tournament.description}</p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-white/60">
          <span>Status: {tournament.status}</span>
          <span>•</span>
          <span>{formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}</span>
          <span>•</span>
          <span>{tournament.country}</span>
        </div>
      </div>

      {/* Header with Create League button */}
      <div className="flex justify-between items-center mb-6">
        <div className="border-b border-white/20">
          <nav className="flex space-x-8">
            {[
              { id: 'teams', label: 'Teams', visible: tournament.teams_visible !== false },
              { id: 'schedule', label: 'Schedule', visible: tournament.schedule_visible !== false },
              { id: 'points_table', label: 'Points table', visible: true },
              { id: 'teams_squad', label: 'Teams Squad', visible: true }
            ]
            .filter(tab => tab.visible)
            .map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-white/60 hover:text-white/80'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <button
          onClick={handleCreateLeague}
          disabled={isLoading}
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            isLoading 
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {isLoading ? 'Loading...' : 'Create League'}
        </button>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === 'schedule' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Match Schedule</h2>
              {userTimezone && (
                <div className="text-sm text-white/60">
                  🌍 Your timezone: {userTimezone}
                </div>
              )}
            </div>
            {matches.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🏏</div>
                <p className="text-white/60 mb-2">No matches scheduled yet</p>
                <p className="text-white/40 text-sm">Match schedule will be available once the tournament begins</p>
              </div>
            ) : (
              matches.map(match => {
                const { dateLabel, utcLine, localLine } = getMatchScheduleLabels(match);
                return (
                <div key={match.id} className="bg-white/10 rounded-lg p-4 border border-white/20">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                      <span className="text-white font-medium break-words">{match.team1_name}</span>
                      <span className="text-white/60 shrink-0">vs</span>
                      <span className="text-white font-medium break-words">{match.team2_name}</span>
                    </div>
                    <div className="text-left sm:text-right shrink-0">
                      {match.venue ? <div className="text-white/60 text-sm">{match.venue}</div> : null}
                      <div className="text-white/60 text-sm">{dateLabel}</div>
                      {(utcLine || localLine) && (
                        <div className="text-blue-300 text-sm font-medium">
                          {utcLine && <span>{utcLine}</span>}
                          {utcLine && localLine && <span className="text-white/50 mx-1">·</span>}
                          {localLine && (
                            <span className="text-green-300">Your time: {localLine}</span>
                          )}
                        </div>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${
                        match.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                        match.status === 'live' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {match.status}
                      </span>
                    </div>
                  </div>
                </div>
              );
              })
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Teams</h2>
            {teams.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🏏</div>
                <p className="text-white/60 mb-2">No teams available yet</p>
                <p className="text-white/40 text-sm">Team information will be available once the tournament begins</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {teams.map(team => (
                  <div 
                    key={team.id} 
                    className="bg-white/10 rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                    <p className="text-white/60 text-sm">{team.short_name}</p>
                    <p className="text-white/60 text-sm">{team.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'points_table' && (() => {
          const rows = pointsSnapshot?.rows;
          const showNrr =
            Array.isArray(rows) && rows.some((r) => r.nrr != null && String(r.nrr).trim() !== '');
          const numCol = (getter: (r: PointsTableRow) => unknown) =>
            Array.isArray(rows) && rows.some((r) => getter(r) != null && getter(r) !== '');
          const showPld = numCol((r) => r.pld);
          const showWon = numCol((r) => r.won);
          const showLost = numCol((r) => r.lost);
          const showTied = numCol((r) => r.tied);
          const showNr = numCol((r) => r.nr);
          const cellNum = (v: number | null | undefined) =>
            v != null && !Number.isNaN(v) ? v : '—';
          return (
            <div className="space-y-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h2 className="text-xl font-semibold text-white">Points table</h2>
                {pointsSnapshot && pointsSnapshot.captured_at && (
                  <p className="text-xs text-white/50">
                    Updated{' '}
                    {new Date(pointsSnapshot.captured_at).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                    {pointsSnapshot.source ? (
                      <span>
                        {' '}
                        · Source: <span className="text-white/70">{pointsSnapshot.source}</span>
                      </span>
                    ) : null}
                  </p>
                )}
              </div>
              {pointsLoading && (
                <div className="text-center py-8 text-white/60">Loading standings…</div>
              )}
              {pointsError && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {pointsError}
                </div>
              )}
              {!pointsLoading && !pointsError && pointsSnapshot === null && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📊</div>
                  <p className="text-white/60 mb-2">No standings yet</p>
                  <p className="text-white/40 text-sm max-w-md mx-auto">
                    The league table will show here after an admin refreshes the points table for this tournament.
                  </p>
                </div>
              )}
              {pointsSnapshot && rows && rows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-white/20">
                  <table className="min-w-full text-sm text-left">
                    <thead>
                      <tr className="bg-white/10 text-white/80">
                        <th className="px-3 py-2 font-medium w-14">#</th>
                        <th className="px-3 py-2 font-medium min-w-[8rem]">Team</th>
                        {showPld && (
                          <th className="px-3 py-2 font-medium text-right w-14" title="Matches played">
                            M
                          </th>
                        )}
                        {showWon && (
                          <th className="px-3 py-2 font-medium text-right w-12" title="Won">
                            W
                          </th>
                        )}
                        {showLost && (
                          <th className="px-3 py-2 font-medium text-right w-12" title="Lost">
                            L
                          </th>
                        )}
                        {showTied && (
                          <th className="px-3 py-2 font-medium text-right w-12" title="Tied">
                            T
                          </th>
                        )}
                        {showNr && (
                          <th className="px-3 py-2 font-medium text-right w-12" title="No result">
                            NR
                          </th>
                        )}
                        <th className="px-3 py-2 font-medium text-right w-16">Pts</th>
                        {showNrr && <th className="px-3 py-2 font-medium text-right w-28">NRR</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr
                          key={`${row.position}-${row.team}-${idx}`}
                          className="border-t border-white/10 text-white bg-white/5 hover:bg-white/[0.07]"
                        >
                          <td className="px-3 py-2 text-white/70">{row.position}</td>
                          <td className="px-3 py-2 font-medium">{row.team}</td>
                          {showPld && (
                            <td className="px-3 py-2 text-right tabular-nums">{cellNum(row.pld)}</td>
                          )}
                          {showWon && (
                            <td className="px-3 py-2 text-right tabular-nums">{cellNum(row.won)}</td>
                          )}
                          {showLost && (
                            <td className="px-3 py-2 text-right tabular-nums">{cellNum(row.lost)}</td>
                          )}
                          {showTied && (
                            <td className="px-3 py-2 text-right tabular-nums">{cellNum(row.tied)}</td>
                          )}
                          {showNr && (
                            <td className="px-3 py-2 text-right tabular-nums">{cellNum(row.nr)}</td>
                          )}
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.pts != null ? row.pts : '—'}
                          </td>
                          {showNrr && (
                            <td className="px-3 py-2 text-right tabular-nums text-white/80">
                              {row.nrr != null && String(row.nrr).trim() !== '' ? row.nrr : '—'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pointsSnapshot && (!rows || rows.length === 0) && !pointsLoading && pointsSnapshot !== null && (
                <div className="text-center py-6 text-white/50 text-sm">Snapshot has no rows.</div>
              )}
            </div>
          );
        })()}

        {activeTab === 'teams_squad' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Team Squads</h2>
            {players.length === 0 ? (
              <div className="text-center py-8 text-white/60">No players available.</div>
            ) : (
              <>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <label className="block text-sm text-white/80 mb-2">Select Team</label>
                  <select
                    value={selectedTeamTab || ''}
                    onChange={(e) => { setSelectedTeamTab(e.target.value); setSelectedRole('All'); }}
                    className="w-full bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from(new Set(players.map((player: any) => player.team_name))).sort().map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </div>

                {selectedTeamTab && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">{selectedTeamTab}</h3>
                      <span className="text-sm text-white/70">
                        {players.filter((p: any) => p.team_name === selectedTeamTab).length} players
                      </span>
                    </div>

                    <div className="space-y-3">
                      {players
                        .filter((player: any) => player.team_name === selectedTeamTab)
                        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                        .map((player: any, idx: number) => {
                          const img = player.image_url || player.image || player.player_img || player.photo || null;
                          return (
                            <div key={player.id || idx} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
                              {img ? (
                                <img
                                  src={img}
                                  alt={player.name}
                                  className="w-10 h-10 rounded-full object-cover border border-white/20"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-semibold">
                                  {player.name?.charAt(0) || '?'}
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg font-semibold text-white">{player.name}</span>
                                  {player.is_foreign && (
                                    <span className="text-orange-400 text-sm ml-1 inline-flex shrink-0" title="Foreign player" aria-label="Foreign player">✈</span>
                                  )}
                                  <span className="text-sm">{getRoleIcon(player.role)}</span>
                                </div>
                                <div className="text-xs text-white/60">
                                  {player.stats && (
                                    <span>
                                      Matches: {player.stats.matches} • Runs: {player.stats.runs} • Wickets: {player.stats.wickets}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}


      </div>

      {/* Create League Modal */}
      {tournament && (
        <CreateLeagueModal
          isOpen={showCreateLeagueModal}
          onClose={() => setShowCreateLeagueModal(false)}
          tournamentId={tournament.id}
          tournamentName={tournament.name}
          onLeagueCreated={handleLeagueCreated}
        />
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        onSwitchToRegister={handleSwitchToRegister}
      />
    </div>
  );
};

export default TournamentDetails;
